import {CHAT_MODERATION_TIMEOUT_MS, CHAT_REJECTION_REASONS} from '../../../configs/matchChat';
import {recordChatSend} from '../../../utils/chat/chatRateLimit';
import {CHAT_CLIENT_REJECTION_REASONS, normalizeModeratorReason} from '../../../utils/chat/getChatRejectionText';
import {MATCH_CHAT_ACTION_TYPES} from './matchChatActions';

/**
 * Модерируемый чат ИИ-матча глазами отправителя (только память клиента).
 *
 * Общая переписка живет в состоянии матча (chatMessages, ADD_CHAT_MESSAGE) —
 * туда попадает только одобренное модератором. Здесь — то, чего в ней еще
 * нет: свои сообщения на пути к модератору и отклоненные (их видит только
 * отправитель), пауза чата и история своих отправок для кулдауна.
 *
 * Жизнь своего сообщения:
 *   SENDING («отправляется…») → PENDING («на проверке у модератора…»)
 *   → одобрено: пришел ADD_CHAT_MESSAGE с тем же clientMessageId — строка
 *     исчезает, сообщение уже в общей переписке;
 *   → отклонено: REJECTED с причиной, пока отправитель не закроет крестиком.
 */

export const MATCH_CHAT_OUTGOING_STATUSES = {
  SENDING: 'sending',
  PENDING: 'pending',
  REJECTED: 'rejected',
};

// Сервер не подтвердил прием за это время — ответ потерялся (обрыв связи):
// сообщение показывается недоставленным, иначе висело бы «отправляется…» и
// занимало место в лимите сообщений на проверке
export const MATCH_CHAT_SEND_ACK_TIMEOUT_MS = 20000;
// Сервер сам отклоняет сообщение, которое модератор не проверил за
// CHAT_MODERATION_TIMEOUT_MS. Запас — на случай, если этот отказ не дошел
export const MATCH_CHAT_PENDING_EXPIRY_MS = CHAT_MODERATION_TIMEOUT_MS + 15000;
// Сколько отказов держим на экране: старые вытесняются новыми
export const MATCH_CHAT_MAX_REJECTED_SHOWN = 5;

const {SENDING, PENDING, REJECTED} = MATCH_CHAT_OUTGOING_STATUSES;

const initialState = {
  isPaused: false,
  // [{clientMessageId, messageId, fromPlayerId, fromPlayerName, text, status,
  //   reason, moderatorReason, retryAfterMs, createdAt, statusAt}]
  outgoing: [],
  // Моменты своих отправок (часы клиента) — для кулдауна (checkChatRateLimit)
  sentAtHistory: [],
  // Сервер отказал «слишком часто»: раньше этого момента (часы клиента) не
  // отправлять
  retryNotBefore: null,
};

const checkIfTimestamp = (value) => typeof value === 'number' && Number.isFinite(value);

const checkIfInFlight = (entry) => entry.status === SENDING || entry.status === PENDING;

// Отказов не больше MATCH_CHAT_MAX_REJECTED_SHOWN: остаются самые свежие
const capRejected = (outgoing) => {
  const rejectedIds = outgoing.filter((entry) => entry.status === REJECTED).map((entry) => entry.clientMessageId);
  if (rejectedIds.length <= MATCH_CHAT_MAX_REJECTED_SHOWN) {
    return outgoing;
  }
  const droppedIds = new Set(rejectedIds.slice(0, rejectedIds.length - MATCH_CHAT_MAX_REJECTED_SHOWN));
  return outgoing.filter((entry) => !droppedIds.has(entry.clientMessageId));
};

const buildEntry = ({clientMessageId, fromPlayerId, fromPlayerName, text}, status, at) => ({
  clientMessageId,
  messageId: null,
  fromPlayerId: fromPlayerId ?? null,
  fromPlayerName: fromPlayerName ?? '',
  text: typeof text === 'string' ? text : '',
  status,
  reason: null,
  moderatorReason: null,
  retryAfterMs: null,
  createdAt: at,
  statusAt: at,
});

// Новое состояние — только если строка нашлась и изменилась
const updateEntry = (state, clientMessageId, update) => {
  let isChanged = false;
  const outgoing = state.outgoing.map((entry) => {
    if (entry.clientMessageId !== clientMessageId) {
      return entry;
    }
    const nextEntry = update(entry);
    isChanged = isChanged || nextEntry !== entry;
    return nextEntry;
  });
  return isChanged ? {...state, outgoing} : state;
};

const removeEntry = (state, clientMessageId) => {
  if (!state.outgoing.some((entry) => entry.clientMessageId === clientMessageId)) {
    return state;
  }
  return {...state, outgoing: state.outgoing.filter((entry) => entry.clientMessageId !== clientMessageId)};
};

// Одобренное сообщение, пропущенное мимо сокета (обрыв связи), приезжает в
// снимке LOAD_STATE. Сверяем по clientMessageId, если снимок его хранит, иначе
// по автору и тексту
const checkIfDeliveredInSnapshot = (entry, chatMessages) => chatMessages.some((message) => {
  if (!message || typeof message !== 'object') return false;
  if (message.clientMessageId) {
    return message.clientMessageId === entry.clientMessageId;
  }
  return message.fromPlayerId === entry.fromPlayerId && message.text === entry.text;
});

const matchChatReducer = (state = initialState, action) => {
  switch (action.type) {
    case MATCH_CHAT_ACTION_TYPES.SEND_STARTED: {
      const {clientMessageId, sentAt} = action.payload || {};
      if (!clientMessageId || state.outgoing.some((entry) => entry.clientMessageId === clientMessageId)) {
        return state;
      }
      const at = checkIfTimestamp(sentAt) ? sentAt : Date.now();
      return {
        ...state,
        outgoing: [...state.outgoing, buildEntry(action.payload, SENDING, at)],
        sentAtHistory: recordChatSend(state.sentAtHistory, at),
      };
    }

    case MATCH_CHAT_ACTION_TYPES.BLOCKED: {
      const {clientMessageId, reason, blockedAt} = action.payload || {};
      if (!clientMessageId || state.outgoing.some((entry) => entry.clientMessageId === clientMessageId)) {
        return state;
      }
      const at = checkIfTimestamp(blockedAt) ? blockedAt : Date.now();
      const entry = {...buildEntry(action.payload, REJECTED, at), reason: reason || null};
      return {...state, outgoing: capRejected([...state.outgoing, entry])};
    }

    case MATCH_CHAT_ACTION_TYPES.PENDING: {
      const {clientMessageId, messageId, receivedAt} = action.payload || {};
      return updateEntry(state, clientMessageId, (entry) => {
        // Запоздалое подтверждение после того, как клиент сам счел ответ
        // потерянным, — сообщение все-таки в очереди: снова «на проверке».
        // Настоящий отказ сервера не отменяется
        const isClientExpired = entry.status === REJECTED
          && entry.reason === CHAT_CLIENT_REJECTION_REASONS.NO_RESPONSE;
        if (entry.status !== SENDING && !isClientExpired) {
          return entry;
        }
        return {
          ...entry,
          status: PENDING,
          messageId: messageId ?? entry.messageId,
          reason: null,
          statusAt: checkIfTimestamp(receivedAt) ? receivedAt : Date.now(),
        };
      });
    }

    case MATCH_CHAT_ACTION_TYPES.REJECTED: {
      const {clientMessageId, messageId, reason, moderatorReason, retryAfterMs, receivedAt} = action.payload || {};
      const at = checkIfTimestamp(receivedAt) ? receivedAt : Date.now();
      const hasRetryAfter = checkIfTimestamp(retryAfterMs) && retryAfterMs > 0;
      // Срок «слишком часто» действует, даже если саму строку уже закрыли
      const retryNotBefore = hasRetryAfter
        ? Math.max(state.retryNotBefore ?? 0, at + retryAfterMs)
        : state.retryNotBefore;
      const nextState = retryNotBefore === state.retryNotBefore ? state : {...state, retryNotBefore};
      const updated = updateEntry(nextState, clientMessageId, (entry) => ({
        ...entry,
        status: REJECTED,
        messageId: messageId ?? entry.messageId,
        reason: reason || null,
        moderatorReason: reason === CHAT_REJECTION_REASONS.MODERATION_REJECTED
          ? normalizeModeratorReason(moderatorReason)
          : null,
        retryAfterMs: hasRetryAfter ? retryAfterMs : null,
        statusAt: at,
      }));
      return updated === nextState ? nextState : {...updated, outgoing: capRejected(updated.outgoing)};
    }

    case MATCH_CHAT_ACTION_TYPES.STATUS_RECEIVED: {
      const isPaused = Boolean(action.payload?.isPaused);
      return isPaused === state.isPaused ? state : {...state, isPaused};
    }

    case MATCH_CHAT_ACTION_TYPES.DISMISSED:
      return removeEntry(state, action.payload?.clientMessageId);

    case MATCH_CHAT_ACTION_TYPES.EXPIRE_STALE: {
      const now = action.payload?.now;
      if (!checkIfTimestamp(now)) return state;
      let isChanged = false;
      const outgoing = state.outgoing.map((entry) => {
        const ageMs = now - entry.statusAt;
        if (entry.status === SENDING && ageMs >= MATCH_CHAT_SEND_ACK_TIMEOUT_MS) {
          isChanged = true;
          return {...entry, status: REJECTED, reason: CHAT_CLIENT_REJECTION_REASONS.NO_RESPONSE, statusAt: now};
        }
        if (entry.status === PENDING && ageMs >= MATCH_CHAT_PENDING_EXPIRY_MS) {
          isChanged = true;
          return {...entry, status: REJECTED, reason: CHAT_REJECTION_REASONS.MODERATION_TIMEOUT, statusAt: now};
        }
        return entry;
      });
      return isChanged ? {...state, outgoing: capRejected(outgoing)} : state;
    }

    // Одобренное сообщение пришло всем, включая отправителя, с его
    // clientMessageId: строка «на проверке» больше не нужна. Сообщения без
    // clientMessageId (немодерируемый чат локальных матчей агентов) сюда не
    // относятся
    case 'ADD_CHAT_MESSAGE': {
      const clientMessageId = action.payload?.clientMessageId;
      return clientMessageId ? removeEntry(state, clientMessageId) : state;
    }

    case 'LOAD_STATE': {
      const chatMessages = action.payload?.loadedState?.gameState?.chatMessages;
      if (!Array.isArray(chatMessages) || !state.outgoing.some(checkIfInFlight)) {
        return state;
      }
      const outgoing = state.outgoing.filter((entry) => !checkIfInFlight(entry)
        || !checkIfDeliveredInSnapshot(entry, chatMessages));
      return outgoing.length === state.outgoing.length ? state : {...state, outgoing};
    }

    // Пауза чата едет и в дескрипторе ИИ-лобби (chat.isPaused). Сообщения
    // сервера идут по порядку, так что последнее из CHAT_STATUS и дескриптора
    // и есть актуальное
    case 'SET_LOBBY':
    case 'LOBBY_UPDATE': {
      const payload = action.payload;
      if (!payload?.isAiMatch || typeof payload.chat?.isPaused !== 'boolean') {
        return state;
      }
      return payload.chat.isPaused === state.isPaused ? state : {...state, isPaused: payload.chat.isPaused};
    }

    case 'LEAVE_LOBBY':
      return initialState;

    default:
      return state;
  }
};

export default matchChatReducer;
